import { 
    DashboardOutlined, 
    PeopleOutlined, 
    LocalHospitalOutlined, 
    SettingsOutlined, 
    ChevronLeft, 
    ChevronRight, 
    MedicalServicesOutlined, 
    EventOutlined, 
    EmojiEventsOutlined, 
    BuildOutlined, 
    DescriptionOutlined, 
    ChatOutlined, 
    MedicationOutlined, 
    BedroomParentOutlined,
    CalendarMonthOutlined
} from "@mui/icons-material";
import { Badge, Box, Divider, Drawer, IconButton, List, ListItem, ListItemButton, ListItemIcon, ListItemText, Toolbar, Avatar } from "@mui/material";
import { useNavigate, Outlet, useLocation } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { useTranslation } from "react-i18next"; // Aggiungi questo import
import useAxiosPrivate from './hooks/useAxiosPrivate';
import useAuth from './hooks/useAuth';
import Header from "./components/Header";
import CalendarPage from "./pages/CalendarPage"; // Importa la pagina del calendario
import getMenuItems from './menuItems';

const Layout = ({ children }) => {
    const expandedDrawerWidth = 240;
    const collapsedDrawerWidth = 64; // Larghezza del drawer quando è contratto
    const [isExpanded, setIsExpanded] = useState(true); // Stato per tracciare se il menu è espanso
    const [mobileOpen, setMobileOpen] = useState(false); // Stato per il menu mobile
    const [tenantLogo, setTenantLogo] = useState(null);
    const drawerWidth = isExpanded ? expandedDrawerWidth : collapsedDrawerWidth;
    // UI feature flags (from localStorage)
    const [uiFeatures, setUiFeatures] = useState({});

    useEffect(() => {
        try {
            const raw = localStorage.getItem('uiFeatures');
            if (raw) {
                setUiFeatures(JSON.parse(raw));
            } else {
                // default: all enabled
                setUiFeatures({ sensors: true, appointments: true, events: true, maintenance: true, weather: true });
            }
        } catch (e) {
            setUiFeatures({ sensors: true, appointments: true, events: true, maintenance: true, weather: true });
        }
        // Listen for cross-component updates in the same SPA
        const handler = (e) => {
            if (!e) return;
            // custom event carries detail
            if (e.detail) {
                setUiFeatures(e.detail);
                return;
            }
        };

        const storageHandler = (e) => {
            if (e.key === 'uiFeatures') {
                try {
                    if (e.newValue) setUiFeatures(JSON.parse(e.newValue));
                } catch (err) {}
            }
        };

        window.addEventListener('uiFeaturesChanged', handler);
        window.addEventListener('storage', storageHandler);

        return () => {
            window.removeEventListener('uiFeaturesChanged', handler);
            window.removeEventListener('storage', storageHandler);
        };
    }, []);

    const isItemVisible = (item) => {
        if (!uiFeatures || Object.keys(uiFeatures).length === 0) return true;
        try {
            const key = item.key || ((item.path || '').split('/').filter(Boolean).slice(-1)[0]);
            if (!key) return true;
            if (key === 'settings') return true; // always visible
            return uiFeatures[key] !== false;
        } catch (e) {
            return true;
        }
    };
   
    const navigate = useNavigate();
    const location = useLocation();
    const { t } = useTranslation("global"); // Inizializza il hook per la traduzione
    const axiosPrivate = useAxiosPrivate();
    const { auth } = useAuth();

    // Carica il logo del tenant
    useEffect(() => {
        const loadTenantLogo = async () => {
            try {
                const response = await axiosPrivate.get('/tenant-settings');
                if (response.data && response.data.logo_path) {
                    setTenantLogo(response.data);
                }
            } catch (error) {
                console.error('Errore caricamento logo tenant:', error);
            }
        };
        
        loadTenantLogo();
    }, []);

    // Ottieni URL del logo tenant
    const getLogoUrl = () => {
        if (tenantLogo?.logo_path && auth?.customer_id) {
            // Usa il proxy Apache invece di accedere direttamente alla porta 4000
            const baseURL = window.location.hostname === 'localhost' 
                ? 'http://localhost:4000' 
                : '';  // URL relativo per usare il proxy Apache
            return `${baseURL}/uploads/customer_${auth.customer_id}/${tenantLogo.logo_path}`;
        }
        // Fallback al logo di default
        return `${process.env.PUBLIC_URL}/images/logo.png`;
    };

    const menuItems = getMenuItems(t);

    // Funzione per alternare lo stato del drawer
    const toggleDrawer = () => {
        setIsExpanded(!isExpanded);
    };

    // Funzione per gestire il menu mobile
    const handleDrawerToggle = () => {
        setMobileOpen(!mobileOpen);
    };

    // Contenuto del drawer (riusato per desktop e mobile)
    const drawerContent = (isMobile = false) => (
        <>
            <Divider />
            <List sx={{ px: 0.5, py: 0 }}>
                {menuItems.map((item) => {
                    if (!isItemVisible(item)) return null;
                    const isSelected = location.pathname === item.path || 
                        (item.path !== `${process.env.REACT_APP_BASE_PATH || '/dashboard'}` && 
                         location.pathname.startsWith(item.path));
                    return (
                        <ListItem
                            key={item.text}
                            disablePadding
                            onClick={() => {
                                navigate(item.path);
                                if (isMobile) setMobileOpen(false);
                            }}
                            title={item.text}
                            sx={{ mb: 0 }}
                        >
                                <ListItemButton selected={isSelected} sx={{ py: 0.4, borderRadius: 1.5, mx: 0.5 }}>
                                <ListItemIcon>
                                    {item.icon}
                                </ListItemIcon>
                                <ListItemText primary={item.text} />
                            </ListItemButton>
                        </ListItem>
                    );
                })}
            </List>
        </>
    );

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh', overflowX: 'hidden' }}>
            {/* Header a tutta larghezza */}
            <Header drawerWidth={drawerWidth} onMenuClick={handleDrawerToggle} />
            
            <Box sx={{ display: 'flex', flexGrow: 1, pt: '104px', overflowX: 'hidden' }}> {/* pt = topbar height + spacing */}
                {/* Drawer sotto l'header */}
                <Box
                    component="nav"
                    sx={{ 
                        width: { sm: drawerWidth }, 
                        flexShrink: { sm: 0 }
                    }}
                    aria-label="mailbox folders"
                >
                {/* Drawer Desktop - Versione collassabile e fisso */}
                <Drawer
                    sx={{
                        display: { xs: "none", sm: "block" },
                        "& .MuiDrawer-paper": {
                            width: drawerWidth,
                            boxSizing: "border-box",
                            overflowX: 'hidden',
                            overflowY: 'auto',
                            whiteSpace: 'nowrap',
                            position: 'fixed',
                            top: 104,
                            left: 0,
                            height: 'calc(100vh - 104px)',
                            border: 'none',
                            borderRight: (theme) => `1px solid ${theme.palette.divider}`,
                            transition: 'width 0.2s ease-in-out',
                            scrollbarWidth: 'thin',
                            scrollbarColor: 'rgba(0,0,0,0.2) transparent',
                            '&::-webkit-scrollbar': {
                                width: '6px',
                            },
                            '&::-webkit-scrollbar-track': {
                                background: 'transparent',
                            },
                            '&::-webkit-scrollbar-thumb': {
                                background: 'rgba(0,0,0,0.2)',
                                borderRadius: '3px',
                            },
                        },
                    }}
                    variant="permanent"
                    anchor="left"
                >
                    {isExpanded ? (
                        <>
                            {drawerContent(false)}
                            <Divider />
                            <Box sx={{ display: 'flex', justifyContent: 'center', p: 1 }}>
                                <IconButton onClick={toggleDrawer}>
                                    <ChevronLeft />
                                </IconButton>
                            </Box>
                        </>
                    ) : (
                        <>
                            <Divider />
                            <List sx={{ px: 0.25, py: 0.5 }}>
                                {menuItems.map((item) => {
                                    if (!isItemVisible(item)) return null;
                                    const isSettingsPath = item.path && item.path.endsWith('/settings');
                                    const isSelected = isSettingsPath ? true : (
                                        location.pathname === item.path || 
                                        (item.path !== `${process.env.REACT_APP_BASE_PATH || '/dashboard'}` && 
                                         location.pathname.startsWith(item.path))
                                    );
                                    return (
                                        <ListItem
                                            key={item.text}
                                            disablePadding
                                            onClick={() => navigate(item.path)}
                                            title={item.text}
                                        >
                                            <ListItemButton 
                                                selected={isSelected}
                                                sx={{ 
                                                    justifyContent: 'center',
                                                    px: 1,
                                                    minHeight: 40,
                                                }}
                                            >
                                                <ListItemIcon sx={{ minWidth: 0, justifyContent: 'center' }}>
                                                    {item.icon}
                                                </ListItemIcon>
                                            </ListItemButton>
                                        </ListItem>
                                    );
                                })}
                            </List>
                            <Divider />
                            <Box sx={{ display: 'flex', justifyContent: 'center', p: 1 }}>
                                <IconButton onClick={toggleDrawer}>
                                    <ChevronRight />
                                </IconButton>
                            </Box>
                        </>
                    )}
                </Drawer>

                {/* Drawer Mobile - Temporary (si apre/chiude con hamburger) */}
                <Drawer
                    variant="temporary"
                    open={mobileOpen}
                    onClose={handleDrawerToggle}
                    ModalProps={{
                        keepMounted: true, // Migliora performance su mobile
                    }}
                    sx={{
                        display: { xs: 'block', sm: 'none' },
                        '& .MuiDrawer-paper': { 
                            boxSizing: 'border-box', 
                            width: expandedDrawerWidth,
                            top: 96,
                            height: 'calc(100% - 96px)'
                        },
                    }}
                >
                    {drawerContent(true)}
                </Drawer>
            </Box>

            {/* Contenuto principale */}
            <Box 
                component="main" 
                sx={{ 
                    flexGrow: 1, 
                    p: 0,
                    width: { xs: '100%', sm: `calc(100% - ${drawerWidth}px)` }
                }}
            >
                <Outlet />
            </Box>
            </Box>
        </Box>
    );
};

export default Layout;